import React, { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";

import { Container, Header, Grid, Button, Form, Label, Loader, Segment, Statistic, InputProps, DropdownProps } from "semantic-ui-react";

import AliancaMenu from "../../components/Menu";
import Footer from "../../components/Footer";

import TableApp, { ITableAppRender } from "../../components/TableApp";

import errors from "../../Utils/ExtractErrors";
import AgentProductivityMonthRepository, { IAgentProductivityMonth, IAgentProductivityMonthParams } from "../../Repositories/AgentProductivityMonthRepository";
import CustomLoader from "../../components/CustomLoader";
import RegionalManagerRepository, { IRegionalManagerListModel } from "../../Repositories/RegionalManagerRepository";
import SectorManagerRepository, { ISectorManagerListModel } from "../../Repositories/SectorManagerRepository";
import AgentsRepository, { IAgentListModel } from "../../Repositories/AgentsRepository";

const AgentMonth: React.FC = () => {

    const [list, setList] = useState<IAgentProductivityMonth[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [state, setState] = useState<IAgentProductivityMonthParams>({ dataInicial: "", dataFinal: "", regionalId: "", sectorId: "", agentId: "" });
    const [regionalManagers, setRegionalManagers] = useState<IRegionalManagerListModel[]>([]);
    const [sectorManagers, setSectorManagers] = useState<ISectorManagerListModel[]>([]);
    const [agents, setAgents] = useState<IAgentListModel[]>([]);

    const totalGeral = list.reduce((a, b) => a + b.quantidade, 0);

    const setColor = (quantidade: number) => {
        if (quantidade >= 10) {
            return <Label color="green">
                {quantidade}
            </Label>
        }
        else {
            return quantidade;
        }
    }

    const load = async () => {

        setLoading(true);
        try {

            const response = await AgentProductivityMonthRepository.AgentProductivityMonth({
                dataInicial: state.dataInicial,
                dataFinal: state.dataFinal.toString(),
                regionalId: state.regionalId,
                sectorId: state.sectorId,
                agentId: state.agentId
            });

            setList(response);
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
        setLoading(false);
    };

    const LoadAgents = useCallback(async (id: string) => {
        const result = await AgentsRepository.List({
            filter: `Status == 1`
        });

        setAgents(result.list);
    }, []);

    useEffect(() => { load() }, []);



    const LoadRegionalManagers = useCallback(async () => {
        const result = await RegionalManagerRepository.List({
            filter: "Status == 1"
        });

        setRegionalManagers(result.list);
    }, []);

    useEffect(() => { LoadRegionalManagers() }, [LoadRegionalManagers]);

    const LoadSectorManagers = useCallback(async (id: string) => {
        const result = await SectorManagerRepository.List({
            filter: `GerenteRegionalId == "${id}" && Status == 1`
        });

        setSectorManagers(result.list);
    }, []);


    const onRegionalManagerChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const { name, value } = data;
        setState(state => ({ ...state, [name]: value, sectorId: "", agentId: "" }));
        LoadSectorManagers(value?.toString() || "");
    }

    const onSectorManagerChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const { name, value } = data;
        setState(state => ({ ...state, [name]: value, agentId: "" }));
        LoadAgents(value?.toString() || "");
    }

    const onDropdownChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const { name, value } = data;
        setState(state => ({ ...state, [name]: value }));
    }

    const onChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: InputProps) => {
        const { name, value } = data;
        setState(state => ({ ...state, [name]: value }));
    }

    const tableRender: ITableAppRender[] = [
        {
            header: "Gerente de Setor",
            content: (x: IAgentProductivityMonth) => x.gerente,
            textAlign: "left",
            width: 6
        },
        {
            header: "Representante",
            content: (x: IAgentProductivityMonth) => x.representante,
            textAlign: "left",
            width: 6
        },
        {
            header: "Quantidade",
            content: (x: IAgentProductivityMonth) => setColor(x.quantidade),
            textAlign: "center",
            width: 1
        }

    ];

    return (
        <>
            <AliancaMenu />
            <Container className="app-margin">
                {loading ? <Segment><CustomLoader /></Segment> :
                    <Grid columns="equal">
                        <Grid.Row>
                            <Grid.Column>
                                <Header size="huge" content="Cadastros por Representante" icon="user plus" />
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column>
                                <Form>
                                    <Form.Group widths="equal">
                                        <Form.Field>
                                            <Form.Input
                                                fluid
                                                label="Data Inicial"
                                                labelPosition="left"
                                                name="dataInicial"
                                                type="date"
                                                value={state.dataInicial}
                                                onChange={onChange}
                                            />
                                        </Form.Field>
                                        <Form.Field>
                                            <Form.Input
                                                fluid
                                                label="Data Final"
                                                labelPosition="left"
                                                name="dataFinal"
                                                type="date"
                                                value={state.dataFinal}
                                                onChange={onChange}
                                            />
                                        </Form.Field>
                                    </Form.Group>
                                    <Form.Group widths="equal">
                                        <Form.Field>
                                            <Form.Dropdown
                                                fluid
                                                selection
                                                search
                                                scrolling
                                                name="regionalId"
                                                noResultsMessage="Nenhum registro encontrado."
                                                placeholder="Selecione"
                                                label="G. Regional"
                                                onChange={onRegionalManagerChange}
                                                options={regionalManagers.map(regionalManager => { return { key: regionalManager.id, value: regionalManager.id, text: regionalManager.nome } })}
                                                value={state.regionalId}
                                                clearable
                                            />
                                        </Form.Field>
                                        <Form.Field>
                                            <Form.Dropdown
                                                fluid
                                                selection
                                                search
                                                scrolling
                                                name="sectorId"
                                                noResultsMessage="Nenhum registro encontrado."
                                                placeholder="Selecione"
                                                label="G. Setor"
                                                onChange={onSectorManagerChange}
                                                options={sectorManagers.map(sectorManager => { return { key: sectorManager.id, value: sectorManager.id, text: sectorManager.nome } })}
                                                value={state.sectorId}
                                                clearable
                                            />
                                        </Form.Field>
                                        <Form.Field>
                                            <Form.Dropdown
                                                fluid
                                                selection
                                                search
                                                scrolling
                                                name="agentId"
                                                noResultsMessage="Nenhum registro encontrado."
                                                placeholder="Selecione"
                                                label="Representante"
                                                onChange={onDropdownChange}
                                                options={agents.map(agent => { return { key: agent.id, value: agent.id, text: agent.nome } })}
                                                value={state.agentId}
                                                clearable
                                            />
                                        </Form.Field>
                                    </Form.Group>
                                </Form>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column>
                                <Button
                                    disabled={
                                        (state.dataInicial !== "" && state.dataFinal !== "") ? false : true
                                    }
                                    fluid
                                    icon="search"
                                    color="pink"
                                    content="Pesquisar"
                                    onClick={load}
                                />
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column>
                                <Segment raised color="teal">
                                    <Statistic.Group widths="1">
                                        <Statistic color="teal">
                                            <Statistic.Value>{totalGeral}</Statistic.Value>
                                            <Statistic.Label content="Total geral"></Statistic.Label>
                                        </Statistic>
                                    </Statistic.Group>
                                </Segment>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Column>
                            <Grid.Row>
                                <TableApp
                                    render={tableRender}
                                    list={list}
                                    loading={loading}
                                />
                            </Grid.Row>
                        </Grid.Column>
                    </Grid>
                }
            </Container>
            <Footer />
        </>
    );
};

export default AgentMonth;
